import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../common/Toast';

const PaymentProcessing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [countdown, setCountdown] = useState(5); // Réduit à 5s pour le test
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Récupérer les données de la transaction
    const state = location.state || {};
    const pendingTransaction = sessionStorage.getItem('aeii_pending_payment');
    
    console.log('📦 Données reçues:', { state, pendingTransaction });
    
    if (pendingTransaction) {
      try {
        const data = JSON.parse(pendingTransaction);
        setPaymentData({
          ...data,
          paymentUrl: state.paymentUrl || data.paymentUrl,
          transactionId: state.transactionId || data.transactionId
        });
        console.log('✅ Données de paiement chargées:', data);
      } catch (e) {
        console.error('❌ Erreur parsing:', e);
        setError('Erreur de chargement des données');
      }
    } else {
      console.warn('⚠️ Aucune transaction en cours');
      setError('Aucune transaction en cours');
    }
  }, [location.state]);

  // Gérer la redirection vers Monetbil
  const redirectToPayment = () => {
    const url = paymentData?.paymentUrl || sessionStorage.getItem('aeii_payment_url');
    
    console.log('🔄 Redirection vers:', url);
    
    if (url && url.startsWith('http')) {
      // Rediriger vers l'URL de paiement
      window.location.href = url;
    } else if (url) {
      // Si c'est une URL relative, naviguer
      navigate(url);
    } else {
      // Pas d'URL, utiliser la simulation
      console.warn('⚠️ Pas d\'URL de paiement, mode simulation');
      simulatePayment();
    }
  };

  // Simuler un paiement réussi (mode test)
  const simulatePayment = async () => {
    try {
      const ref = paymentData?.transactionId || sessionStorage.getItem('aeii_transaction_id');
      
      if (ref) {
        console.log('🔄 Simulation de confirmation pour:', ref);
        
        const response = await fetch('http://localhost:5000/api/payment/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reference: ref })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Paiement simulé avec succès:', data);
          await refreshUser();
          sessionStorage.removeItem('aeii_pending_payment');
          sessionStorage.removeItem('aeii_payment_url');
          sessionStorage.removeItem('aeii_transaction_id');
          
          navigate('/payment-success', { 
            state: { 
              plan: paymentData?.plan || 'classique',
              amount: paymentData?.amount || 15000
            } 
          });
          return;
        }
      }
      
      // Fallback: rediriger vers la page de succès
      navigate('/payment-success', { 
        state: { 
          plan: paymentData?.plan || 'classique',
          amount: paymentData?.amount || 15000
        } 
      });
    } catch (error) {
      console.error('❌ Erreur simulation:', error);
      setError('Erreur lors de la simulation');
    }
  };

  // Démarrer le compte à rebours
  useEffect(() => {
    if (!paymentData && !error) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Rediriger vers le paiement quand le compte à rebours est terminé
          redirectToPayment();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [paymentData, error]);

  // Vérifier périodiquement le statut du paiement
  useEffect(() => {
    const checkStatus = async () => {
      const ref = paymentData?.transactionId || sessionStorage.getItem('aeii_transaction_id');
      if (!ref) return;

      try {
        const response = await fetch(`http://localhost:5000/api/payment/status/${ref}`);
        const data = await response.json();

        console.log('📊 Statut du paiement:', data);

        if (data.status === 'completed') {
          setStatus('success');
          await refreshUser();
          showToast('🎉 Paiement confirmé ! Abonnement activé');
          
          sessionStorage.removeItem('aeii_pending_payment');
          sessionStorage.removeItem('aeii_payment_url');
          sessionStorage.removeItem('aeii_transaction_id');
          
          setTimeout(() => {
            navigate('/payment-success', { 
              state: { 
                plan: paymentData?.plan || data.plan,
                amount: paymentData?.amount || data.amount
              } 
            });
          }, 1500);
        }
      } catch (error) {
        console.error('❌ Erreur vérification:', error);
      }
    };

    const interval = setInterval(checkStatus, 5000);
    checkStatus();

    return () => clearInterval(interval);
  }, [paymentData, navigate, refreshUser]);

  const handleManualRedirect = () => {
    redirectToPayment();
  };

  const getPlanLabel = (plan) => {
    const labels = {
      classique: 'Classique',
      premium: 'Premium'
    };
    return labels[plan] || plan;
  };

  if (error) {
    return (
      <section className="section">
        <div className="container text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ 
            padding: '40px 30px',
            background: '#fff5f5',
            borderRadius: '16px',
            border: '1px solid #fed7d7'
          }}>
            <i className="fa-solid fa-circle-exclamation" style={{ fontSize: '3rem', color: '#fc8181' }}></i>
            <h3 style={{ marginTop: '16px', color: '#742a2a' }}>Erreur</h3>
            <p style={{ color: '#9b2c2c' }}>{error}</p>
            <Link to="/abonnements" className="btn btn-dark" style={{ marginTop: '16px' }}>
              <i className="fa-solid fa-arrow-left"></i> Retour aux abonnements
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (status === 'success') {
    return (
      <section className="section">
        <div className="container text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ 
            padding: '40px 30px',
            background: '#f0fff4',
            borderRadius: '16px',
            border: '1px solid #c6f6d5'
          }}>
            <i className="fa-solid fa-circle-check" style={{ fontSize: '3rem', color: '#48bb78' }}></i>
            <h2 style={{ marginTop: '16px', color: '#22543d' }}>Paiement confirmé !</h2>
            <p style={{ color: '#2f855a' }}>
              Votre abonnement <strong>{getPlanLabel(paymentData?.plan)}</strong> est maintenant actif.
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Link to="/mon-compte" className="btn btn-dark">
                <i className="fa-solid fa-user"></i> Voir mon compte
              </Link>
              <Link to="/publications" className="btn btn-primary">
                <i className="fa-solid fa-book"></i> Explorer
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="container text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ 
          padding: '40px 30px',
          background: '#ebf8ff',
          borderRadius: '16px',
          border: '1px solid #bee3f8'
        }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#3182ce' }}></i>
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#3182ce'
            }}>
              {countdown}
            </span>
          </div>

          <h2 style={{ marginTop: '24px', color: '#2a4365' }}>
            En cours de validation
          </h2>
          
          <p style={{ color: '#2c5282' }}>
            {countdown > 0 ? (
              <>Redirection vers la page de paiement dans <strong>{countdown}</strong> secondes...</>
            ) : (
              'Redirection en cours...'
            )}
          </p>

          <div style={{ 
            margin: '20px 0',
            padding: '16px',
            background: 'rgba(255,255,255,0.5)',
            borderRadius: '8px',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#4a5568' }}>Abonnement</span>
              <span style={{ fontWeight: '600' }}>{getPlanLabel(paymentData?.plan)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#4a5568' }}>Montant</span>
              <span style={{ fontWeight: '700', color: '#0c2340' }}>
                {paymentData?.amount?.toLocaleString() || '0'} FCFA
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
              <span style={{ color: '#4a5568' }}>Statut</span>
              <span style={{ color: '#ecc94b' }}>
                <i className="fa-solid fa-clock"></i> En attente
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {countdown === 0 && (
              <button className="btn btn-primary" onClick={handleManualRedirect}>
                <i className="fa-solid fa-arrow-right"></i> Continuer vers le paiement
              </button>
            )}
            
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={simulatePayment}
              style={{ fontSize: '0.8rem' }}
            >
              <i className="fa-solid fa-flask"></i> Simuler le paiement (test)
            </button>
          </div>

          <div style={{ marginTop: '16px', fontSize: '0.85rem', color: '#718096' }}>
            <p>Vous allez être redirigé vers la plateforme de paiement sécurisé Monetbil.</p>
            <p style={{ marginTop: '8px', fontSize: '0.75rem' }}>
              <i className="fa-solid fa-info-circle"></i> Si la redirection ne fonctionne pas, cliquez sur "Continuer vers le paiement"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentProcessing;